import { useEffect, useRef, useState } from "react";
import { useApi } from "@/lib/cache";
import { LoadState } from "@/components/LoadState";
import { Link, useNavigate } from "react-router-dom";
import { setPendingUpload } from "@/lib/pendingUpload";
import { thumbUrl } from "@/lib/api";
import { ArtworkPlaceholder } from "@/components/ArtworkPlaceholder";
import { ChevronRight, Play, Pause, Archive, SkipBack, SkipForward, Radio, Plus, Globe, Flame } from "lucide-react";
import { usePlayer, getRecent } from "@/context/PlayerContext";
import { NewItemDialog } from "@/components/NewItemDialog";
import { useAuth } from "@/context/AuthContext";

function pct(n) { return `${Math.round(n || 0)}%`; }
// Local-time M.D.YYYY (no UTC shift).
function mdy(iso) {
  const d = new Date(iso);
  if (!iso || isNaN(d)) return null;
  return `${d.getMonth() + 1}.${d.getDate()}.${d.getFullYear()}`;
}
function lastUpdateLabel(s) {
  const upd = mdy(s.last_activity);
  if (upd) return `Last Update: ${upd}`;
  const created = mdy(s.created_at);
  return created ? `Created: ${created}` : "No date recorded";
}
const lastTouched = (s) => s.last_activity || s.created_at || "";
function fmtTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function Inicio() {
  const songsQ = useApi("/songs");
  const archivedQ = useApi("/songs?archived=true");
  const songs = songsQ.data || [];
  const archived = archivedQ.data || [];
  const loading = songsQ.loading || archivedQ.loading;
  const loadError = songsQ.error || archivedQ.error;
  const retry = () => { songsQ.reload(); archivedQ.reload(); };
  const [recentSongs, setRecentSongs] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const { current, playing, toggle, progress, duration, playSong, prevTrack, nextTrack, queue } = usePlayer();

  useEffect(() => {
    const recentIds = getRecent().map((r) => r.id);
    const map = Object.fromEntries(songs.map((s) => [s.id, s]));
    setRecentSongs(recentIds.map((id) => map[id]).filter(Boolean).slice(0, 5));
  }, [songsQ.data, current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Oldest untouched unfinished songs first (last_activity only moves on real edits).
  const needsAttention = songs
    .filter((s) => !s.archived && !s.released && (s.progress || 0) < 100)
    .sort((a, b) => lastTouched(a).localeCompare(lastTouched(b)))
    .slice(0, 5);
  const pctPlay = duration ? (progress / duration) * 100 : 0;

  return (
    <div className="p-4 md:p-10 max-w-6xl mx-auto animate-in hq-page-wrap overflow-x-hidden">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-zinc-500 uppercase tracking-widest">LXN HQ</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Home</h1>
        </div>
        {isOwner && (
          <button onClick={() => setOpenNew(true)} data-testid="home-add-btn" aria-label="Add new"
                  className="shrink-0 h-11 w-11 rounded-full bg-violet-600 hover:bg-violet-500 text-white flex items-center justify-center shadow-lg shadow-violet-900/30">
            <Plus className="w-5 h-5" />
          </button>
        )}
      </header>
      <NewItemDialog open={openNew} onOpenChange={setOpenNew} />

      {!loading && !loadError && <Overview songs={songs} archivedCount={archived.length} />}

      {/* Phase 6.8d — LXNWRLD Posting Tracker.
          Compact accountability widget driven by the SAME `wrld_media`
          records the public feed uses. Tapping the card opens the LXN WRLD
          manager where posts are added. */}
      {isOwner && <PostingTracker />}

      {/* Now Playing OR Recently Played */}
      {current ? (
        <section className="mb-8" data-testid="home-now-playing">
          <h2 className="text-xs text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-violet-400" /> Now Playing
          </h2>
          <div className="p-4 rounded-xl bg-[#141416] border border-white/5 flex items-center gap-4">
            <ArtworkPlaceholder title={current.song?.name || ""} artworkId={current.song?.artwork_id} size="lg" />
            <div className="flex-1 min-w-0">
              <Link to={`/songs/${current.song?.id}`} className="block max-w-full text-lg font-semibold truncate hover:text-violet-300">
                {current.song?.name}
              </Link>
              <div className="flex items-center gap-2 mt-0.5 min-w-0">
                <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-violet-600/20 text-violet-300 truncate max-w-full">{current.version?.label}</span>
                {queue?.length > 1 && <span className="hidden sm:inline text-[10px] text-zinc-500 shrink-0">In queue</span>}
              </div>
              <div className="mt-2 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500" style={{ width: `${pctPlay}%` }} />
              </div>
              <div className="flex justify-between mt-1 text-[10px] mono text-zinc-500">
                <span>{fmtTime(progress)}</span><span>{fmtTime(duration)}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={prevTrack} data-testid="home-prev" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300"><SkipBack className="w-4 h-4" /></button>
              <button onClick={toggle} data-testid="home-toggle" className="w-11 h-11 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center text-white">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <button onClick={nextTrack} data-testid="home-next" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300"><SkipForward className="w-4 h-4" /></button>
            </div>
          </div>
        </section>
      ) : (
        <section className="mb-8" data-testid="home-recently-played">
          <h2 className="text-xs text-zinc-400 uppercase tracking-widest mb-3">Recently played</h2>
          {recentSongs.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {recentSongs.map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#141416] border border-white/5 hover:border-white/10 transition-colors">
                  <button onClick={() => playSong(s)} data-testid={`recent-play-${s.id}`}
                    className="shrink-0 w-11 h-11 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center text-white">
                    <Play className="w-4 h-4 ml-0.5" />
                  </button>
                  <Link to={`/songs/${s.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                    <ArtworkPlaceholder title={s.name} artworkId={s.artwork_id} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="mono text-[10px] text-zinc-500">{Math.round(s.progress || 0)}% · {s.released ? "Released" : (s.progress >= 100 ? "Completed" : "In Progress")}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 rounded-xl bg-[#101012] border border-dashed border-white/10 text-sm text-zinc-500 text-center">
              Nothing playing yet. Open any song to start listening.
            </div>
          )}
        </section>
      )}

      {!loading && !loadError && <RecentlyUpdated songs={songs} />}

      <UpcomingEvents />

      {!loading && !loadError && <UpcomingReleases songs={songs} />}

      {(loading || loadError) ? (
        <LoadState loading={loading} error={loadError} onRetry={retry} testid="home" />
      ) : needsAttention.length > 0 && (
        <section className="mb-8" data-testid="home-needs-attention">
          <h2 className="text-xs text-zinc-400 uppercase tracking-widest mb-3">Needs attention</h2>
          <div className="space-y-2">
            {needsAttention.map((s) => (
              <Link key={s.id} to={`/songs/${s.id}`} data-testid={`needs-attention-${s.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-[#141416] border border-white/5 hover:border-white/10">
                <ArtworkPlaceholder title={s.name} artworkId={s.artwork_id} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.name}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`needs-attention-since-${s.id}`}>{lastUpdateLabel(s)}</div>
                  <div className="h-1 bg-zinc-800 rounded-full overflow-hidden mt-1.5">
                    <div className="h-full bg-violet-500" style={{ width: `${s.progress || 0}%` }} />
                  </div>
                </div>
                <span className="mono text-xs text-violet-300 tabular">{pct(s.progress)}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {archived.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-zinc-400 uppercase tracking-widest">Recent archive</h2>
            <Link to="/archive" className="text-xs text-violet-400 hover:text-violet-300">View all</Link>
          </div>
          <div className="space-y-2">
            {archived.slice(0, 3).map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#141416] border border-white/5">
                <Archive className="w-4 h-4 text-zinc-500" />
                <div className="text-sm text-zinc-300 truncate flex-1">{s.name}</div>
                <div className="text-xs text-zinc-500">{s.archive_reason}</div>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
}

/* =====================================================================
 * UPCOMING — next events from the existing calendar (calendar_events).
 * ===================================================================*/
const EVENT_TYPES = { release: "Release", recording: "Recording", deadline: "Deadline", content: "Content", personal: "Personal", other: "Other" };
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
function daysUntil(key) {
  const [y, m, d] = key.split("-").map(Number);
  const now = new Date();
  return Math.round((new Date(y, m - 1, d) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
}
function fmt12(t) {
  if (!t) return "";
  const [h, mi] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(mi).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

function UpcomingEvents() {
  const { data, loading, error, reload } = useApi("/wrld/events");
  const today = todayKey();
  const upcoming = (data || [])
    .filter((e) => e.event_date && !e.completed)
    .sort((a, b) => (a.event_date + (a.start_time || "")).localeCompare(b.event_date + (b.start_time || "")))
    .slice(0, 5);
  return (
    <section className="mb-8" data-testid="home-upcoming">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs text-zinc-400 uppercase tracking-widest">Upcoming</h2>
        <Link to="/wrld/calendar" data-testid="upcoming-view-calendar" className="text-[10px] tracking-widest uppercase text-violet-300 hover:text-violet-200">View calendar</Link>
      </div>
      {(loading || error) ? <LoadState loading={loading} error={error} onRetry={reload} testid="upcoming" /> : upcoming.length === 0 ? (
        <div className="p-4 rounded-xl bg-[#101012] border border-dashed border-white/10 text-sm text-zinc-500 text-center" data-testid="upcoming-empty">Nothing scheduled.</div>
      ) : (
        <div className="space-y-2">
          {upcoming.map((e) => {
            const n = daysUntil(e.event_date);
            return (
              <Link key={e.id} to={`/wrld/calendar?event=${e.id}`} data-testid={`upcoming-${e.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-[#141416] border border-white/5 hover:border-white/10">
                <div className="w-11 shrink-0 text-center">
                  <div className="mono text-lg leading-none text-white">{Number(e.event_date.slice(8, 10))}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{new Date(e.event_date + "T00:00:00").toLocaleString([], { month: "short" })}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{e.title}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 truncate" data-testid={`upcoming-meta-${e.id}`}>
                    {EVENT_TYPES[e.category] || "Other"} · {mdy(e.event_date + "T00:00:00")}{e.start_time ? ` · ${fmt12(e.start_time)}` : ""}
                  </div>
                </div>
                <span className={`mono text-[10px] shrink-0 ${n < 0 ? "text-amber-300" : "text-violet-300"}`} data-testid={`upcoming-days-${e.id}`}>
                  {n < 0 ? "Overdue" : n === 0 ? "Today" : n === 1 ? "Tomorrow" : `in ${n} days`}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* =====================================================================
 * DASHBOARD CARDS (overview, recently updated, upcoming releases)
 * ===================================================================*/
function Overview({ songs, archivedCount }) {
  const stats = [
    { k: "total", label: "Total songs", n: songs.length + archivedCount, to: "/catalog" },
    { k: "progress", label: "In progress", n: songs.filter((s) => !s.released && (s.progress || 0) < 100).length, to: "/songs?filter=wip" },
    { k: "completed", label: "Completed", n: songs.filter((s) => !s.released && (s.progress || 0) >= 100).length, to: "/songs?filter=done" },
    { k: "released", label: "Released", n: songs.filter((s) => s.released).length, to: "/releases" },
  ];
  return (
    <section className="mb-6 grid grid-cols-4 gap-2" data-testid="home-overview">
      {stats.map((s) => (
        <Link key={s.k} to={s.to} data-testid={`home-stat-${s.k}`}
          className="p-3 rounded-xl bg-[#141416] border border-white/5 hover:border-violet-500/30 transition-colors min-w-0">
          <div className="mono text-xl md:text-2xl font-bold tabular leading-none" data-testid={`home-stat-${s.k}-value`}>{s.n}</div>
          <div className="text-[9px] md:text-[10px] tracking-wide uppercase text-zinc-500 mt-1.5 leading-tight">{s.label}</div>
        </Link>
      ))}
    </section>
  );
}

function RecentlyUpdated({ songs }) {
  const list = [...songs].filter((s) => s.last_activity).sort((a, b) => b.last_activity.localeCompare(a.last_activity)).slice(0, 4);
  return (
    <section className="mb-8" data-testid="home-recently-updated">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs text-zinc-400 uppercase tracking-widest">Recently updated</h2>
        <Link to="/catalog" data-testid="home-catalog-link" className="text-[10px] tracking-widest uppercase text-violet-300 hover:text-violet-200">Open catalog</Link>
      </div>
      {list.length === 0 ? <div className="p-4 rounded-xl bg-[#101012] border border-dashed border-white/10 text-sm text-zinc-500 text-center">No updates yet.</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {list.map((s) => (
            <Link key={s.id} to={`/songs/${s.id}`} data-testid={`recent-updated-${s.id}`}
              className="flex items-center gap-3 p-2.5 rounded-lg bg-[#141416] border border-white/5 hover:border-white/10">
              <ArtworkPlaceholder title={s.name} artworkId={s.artwork_id} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{s.name}</div>
                <div className="text-[10px] text-zinc-500">{lastUpdateLabel(s)} · {pct(s.progress)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function UpcomingReleases({ songs }) {
  const today = todayKey();
  const list = songs
    .filter((s) => s.release_info?.release_date && s.release_info.release_date >= today && s.release_info.release_status !== "released" && !s.released)
    .sort((a, b) => a.release_info.release_date.localeCompare(b.release_info.release_date))
    .slice(0, 4);
  if (!list.length) return null;
  return (
    <section className="mb-8" data-testid="home-upcoming-releases">
      <h2 className="text-xs text-zinc-400 uppercase tracking-widest mb-3">Upcoming releases</h2>
      <div className="space-y-2">
        {list.map((s) => {
          const n = daysUntil(s.release_info.release_date);
          return (
            <Link key={s.id} to={`/songs/${s.id}?tab=release`} data-testid={`upcoming-release-${s.id}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-[#141416] border border-white/5 hover:border-white/10">
              <ArtworkPlaceholder title={s.name} artworkId={s.artwork_id} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{s.name}</div>
                <div className="text-[10px] text-zinc-500 capitalize">{s.release_info.release_status || "planned"} · {mdy(s.release_info.release_date + "T00:00:00")}</div>
              </div>
              <span className="mono text-[10px] text-violet-300 shrink-0">{n === 0 ? "Today" : n === 1 ? "Tomorrow" : `in ${n} days`}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* =====================================================================
 * POSTING TRACKER (Phase 6.8d)
 * - Fetches `/wrld/posting-stats` with the browser's current tz offset so
 *   dates are grouped on the user's LOCAL calendar.
 * - Shows TODAY count, consecutive-day STREAK, and a 7-day visualization
 *   (oldest → today). Weekday initials match the dates returned by the
 *   backend so the grid always starts on the correct day.
 * - Tapping anywhere on the card routes to /wrld (existing posting flow).
 * ===================================================================== */
function PostingTracker() {
  const [tz] = useState(() => new Date().getTimezoneOffset()); // minutes west of UTC
  const { data: stats } = useApi(`/wrld/posting-stats?tz_offset=${tz}`);
  const { data: media } = useApi("/wrld/media");
  const latest = [...(media || [])].filter((m) => m.published !== false).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  const fileRef = useRef(null);
  const navigate = useNavigate();
  if (!stats) return null;
  const DOW = ["M","T","W","T","F","S","S"];
  const today = stats.today_count || 0;
  const streak = stats.streak || 0;
  const emptyToday = today === 0;
  return (
    <Link to="/wrld/calendar" data-testid="home-posting-tracker"
          className="block mb-8 group">
      <section className="p-4 rounded-xl bg-[#141416] border border-white/5 group-hover:border-violet-500/30 transition-colors">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] text-violet-400 uppercase">
              <Globe className="w-3.5 h-3.5" /> LXNWRLD
            </div>
            <div className="mt-2 flex items-baseline gap-4">
              <div data-testid="posting-today">
                <div className="mono text-2xl font-bold tabular leading-none">{today}</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-1">Today</div>
              </div>
              <div data-testid="posting-streak">
                <div className="mono text-2xl font-bold tabular leading-none flex items-center gap-1.5">
                  {streak}
                  {streak > 0 && <Flame className="w-4 h-4 text-amber-400" />}
                </div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-1">
                  {streak === 1 ? "day streak" : "day streak"}
                </div>
              </div>
            </div>
          </div>
          <button type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); fileRef.current?.click(); }}
                className="shrink-0 h-9 px-3 rounded-full bg-violet-600/15 border border-violet-500/30 text-[10px] tracking-widest text-violet-200 uppercase flex items-center gap-1.5 hover:bg-violet-600/25"
                data-testid="posting-add-shortcut">
            <Plus className="w-3.5 h-3.5" /> Post
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" data-testid="posting-add-file-input"
                 onClick={(e) => e.stopPropagation()}
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setPendingUpload(f); navigate("/wrld"); } }} />
        </div>
        <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-7 gap-1.5" data-testid="posting-week">
          {stats.week?.map((d, i) => (
            <div key={d.date} data-testid={`posting-day-${d.date}`}
                 className="flex flex-col items-center gap-1">
              <span className="text-[9px] tracking-widest text-zinc-500">{DOW[d.weekday]}</span>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] mono ${
                d.has_post
                  ? "bg-violet-500/80 text-white"
                  : (i === stats.week.length - 1 ? "border border-dashed border-zinc-700 text-zinc-700" : "bg-white/5 text-zinc-700")
              }`}>
                {d.has_post ? "✓" : "○"}
              </span>
            </div>
          ))}
        </div>
        {emptyToday && streak > 0 && (
          <div className="mt-2 text-[10px] text-zinc-500">Post to continue your {streak}-day streak.</div>
        )}
        {latest && (
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-3" data-testid="posting-latest">
            <img src={thumbUrl(latest.poster_file_id || latest.file_id || latest.media_file_id, 160)} alt="" loading="lazy"
                 onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                 className="w-10 h-10 rounded-md object-cover bg-white/5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Latest post</div>
              <div className="text-xs text-zinc-300 truncate">{latest.caption || (latest.kind === "video" ? "Video" : "Photo")} · {mdy(latest.created_at)}</div>
            </div>
          </div>
        )}
      </section>
    </Link>
  );
}

